import {
    noCompanyGroupHeader,
    noCityGroupHeader,
    noDateGroupHeader,
} from './personaListSelectorUtils.locstring.json';
import loc from 'owa-localize';
import {
    HubPersona,
    sortBy,
    getFullDisplayName,
    secondaryTextComparer,
    createMapOfSet,
    HubListEntryKind,
    PersonType,
    FavoriteEntity,
    makeHubPersonaId,
    Person,
    PrivateDistributionList,
    DeletedPersona,
    makeHubGroupPersonaId,
    UnifiedGroup,
} from '../../shared';
import {
    compareByGivenNameAndSurname,
    compareBySurnameAndGivenName,
    compareByRelevancy,
    compareByWorkCity,
    compareByHomeCity,
    compareByRecentlyAdded,
    compareByDeletedDate,
} from '../utils/personaSorting';
import { isSortingWithInitials } from '../utils/personaListSortUtils';
import { getGuid } from 'owa-guid';
import type {
    PersonaListEntry,
    PersonaEntry,
    PersonaGroup,
} from '../selectors/personaListSelector';

import { initial } from '../utils/initial';
import {
    HubPersonasMap,
    SearchResultState,
    Filter,
    Sorting,
    UnifiedGroupsMap,
} from '../../appstate';
import type { PersonaListViewModel } from '../models/PersonaListViewModel';
import { userDate, formatUserDate } from 'owa-datetime';
import type { MoveContactsState } from '../state/moveContactsState';
import { PersonaListLoadingState } from '../models/personaListLoadingState';
import DisplayOrder from 'owa-people-types/lib/types/DisplayOrder';

// Threshold for alphabetical lists to not display the group
const NO_GROUP_THRESHOLD = 10;

export const NO_INITIAL_INDICATOR = '🞼';

/**
 * Returns an adjusted group label. When sorting on date, the date has to be presented according to the OWA user config settings.
 * @param groupLabel The original group label. If sorting is by date, the label will be in ISO format YYYY-MM-DD.
 * @param sorting The sort type
 */
const adjustedGroupLabel = (groupLabel: string, sorting: Sorting): string => {
    return sorting === Sorting.ByRecentlyAdded || sorting === Sorting.ByDeletedDate
        ? formatUserDate(userDate(groupLabel))
        : groupLabel;
};

/**
 * Returns the YYYY-MM-DD date part of an ISO timestamp used for sorting on date.
 * If year starts with zero (0001), return the empty marker, as the timestamp is not a valid one.
 * @param timestamp A timestamp in ISO format
 */
const dateForSorting = (timestamp: string, emptyMarker: string): string => {
    return timestamp
        ? timestamp[0] === '0'
            ? emptyMarker
            : timestamp.substring(0, 10)
        : emptyMarker;
};

// We use a custom comparer for the personaMap to avoid re-rendering if attributes change that are not displayed in the list
export function isPersonaMapsEqual<PersonasMap extends {}>(
    map1: PersonasMap,
    map2: PersonasMap
): boolean {
    if (map1 === map2) {
        return true;
    }

    const map1Keys = Object.keys(map1);
    if (map1Keys.length !== Object.keys(map2).length) {
        return false;
    }

    return map1Keys.every(personaId => {
        const entry1 = map1[personaId];
        const entry2 = map2[personaId];

        if (!entry2) {
            return false;
        }

        const displayName1 = getFullDisplayName(entry1, DisplayOrder.FirstNameLastName);
        const displayName2 = getFullDisplayName(entry2, DisplayOrder.FirstNameLastName);

        return (
            displayName1 === displayName2 &&
            entry1.creationTime === entry2.creationTime &&
            secondaryTextComparer(entry1, displayName1, entry2, displayName2)
        );
    });
}

/**
 * Extracts list of search result personas, and applies relevancyScore.
 */
export function extractPersonasWithRelevancyScore(
    personasMap: HubPersonasMap,
    groupsMap: UnifiedGroupsMap,
    searchResult: SearchResultState
): HubPersona[] {
    return searchResult?.personaEmailMap
        ? Object.keys(searchResult.personaEmailMap)
              .map(hubPersonaId => {
                  const personaMatch = personasMap[hubPersonaId];
                  const groupsMatch = groupsMap[hubPersonaId];

                  return (
                      hubPersonaId && {
                          ...(personaMatch || groupsMatch),
                          relevancyScore: searchResult.personaEmailMap[hubPersonaId].relevancyScore,
                      }
                  );
              })
              .filter(persona => persona.type)
        : [];
}

/**
 * Applies filtering to list of entries based on a viewState
 */
export function filterPersonasAndRemoveDeleted(
    personas: HubPersona[],
    personaListViewModel: PersonaListViewModel,
    personasBeingDeleted: string[]
): HubPersona[] {
    let filteredPersonas = filterPersonas(personas, personaListViewModel.filter);

    if (personasBeingDeleted.length > 0) {
        const personasBeingDeletedMap = createMapOfSet(personasBeingDeleted);
        filteredPersonas = filteredPersonas.filter(
            persona => persona && !personasBeingDeletedMap[persona.hubPersonaId]
        );
    }

    return filteredPersonas || [];
}

/**
 * Removes Deleted groups from entries
 */
export function removeDeletedGroups(
    personas: HubPersona[],
    knownGroupsMap: UnifiedGroupsMap
): HubPersona[] {
    const knownGroups = Object.keys(knownGroupsMap).map(key => knownGroupsMap[key]);
    let filteredPersonas = personas.filter(persona => {
        if (persona.type === PersonType.GroupBasicData || persona.type === PersonType.Group) {
            const directoryId = persona.externalDirectoryObjectId;
            const matchingGroup = knownGroups.find(
                group => !!directoryId && group.externalDirectoryObjectId === directoryId
            );
            return !matchingGroup || !matchingGroup.whenSoftDeletedTime;
        }
        return !!persona;
    });
    return filteredPersonas;
}

/**
 * Applies filtering and modification of entries based on contacts curentely being in Move state
 * It's possible that the persona in toFolderId is already there, so there should be extra logic to not duplicate
 */
export function updatedWithContactsInMove(
    personas: HubPersona[],
    moveContactsState: MoveContactsState,
    currentFolderId: string
): HubPersona[] {
    if (
        !personas ||
        !personas.length ||
        !moveContactsState.personas ||
        !moveContactsState.personas.length
    ) {
        return personas;
    }

    if (
        moveContactsState.fromFolderId !== currentFolderId &&
        moveContactsState.toFolderId !== currentFolderId
    ) {
        return personas;
    }

    const contactsBeingMovedMap = createMapOfSet(
        moveContactsState.personas.map(contact => contact.hubPersonaId)
    );

    const filteredPersonas = personas.filter(
        persona => !contactsBeingMovedMap[persona.hubPersonaId]
    );

    if (moveContactsState.toFolderId === currentFolderId) {
        return filteredPersonas.concat(moveContactsState.personas);
    }

    return filteredPersonas;
}

/**
 * Uses the group function to insert group headers where appropriate for the entries array.
 * Alphabetical sorted lists with NO_GROUP_THRESHOLD or less items are not grouped.
 * Sorting is performed on each group (sorting here instead of passing sorted $entries will minimise the number of comparisons done)
 */
export function applyGroupingAndSorting(
    entries: HubPersona[],
    personaListViewModel: PersonaListViewModel,
    culture: string
): PersonaListEntry[] {
    type GroupToPersonasMapping = { [group: string]: PersonaEntry[] };

    const sorting = personaListViewModel.sorting;

    // Do not group if sorting by name and less than threshold, or sorted by relevance
    if (
        (isSortingWithInitials(sorting) && entries.length <= NO_GROUP_THRESHOLD) ||
        sorting === Sorting.ByRelevance
    ) {
        return entries
            .filter(entry => !!entry)
            .map(toPersonaListElement)
            .sort((a, b) =>
                getComparePersonasFunction(
                    sorting,
                    culture,
                    personaListViewModel.displayOrder
                )(a.value, b.value)
            );
    }

    // empty entries are marked with a guid and replaced with a label after grouping
    const emptyMarker = getGuid();
    const group = groupingFunction(sorting, emptyMarker, culture);

    const groupedEntries: GroupToPersonasMapping = entries.reduce<GroupToPersonasMapping>(
        (groupedEntries: GroupToPersonasMapping, current: HubPersona): GroupToPersonasMapping => {
            const currentGroup = group(current);

            if (groupedEntries.hasOwnProperty(currentGroup)) {
                groupedEntries[currentGroup].push(toPersonaListElement(current));
            } else {
                groupedEntries[currentGroup] = [toPersonaListElement(current)];
            }

            return groupedEntries;
        },
        {}
    );

    const doReverseSorting =
        sorting === Sorting.ByRecentlyAdded || sorting === Sorting.ByDeletedDate;
    const sortedKeys = Object.keys(groupedEntries)
        .filter(groupHeader => groupHeader !== emptyMarker)
        .sort(
            sortBy(
                culture,
                header => [header],
                header => header,
                doReverseSorting
            )
        )
        .concat(groupedEntries[emptyMarker] ? [emptyMarker] : []);

    // Sorting within-group is performed.
    Object.keys(groupedEntries).forEach(
        key =>
            (groupedEntries[key] = groupedEntries[key].sort((a, b) =>
                getComparePersonasFunction(
                    sorting,
                    culture,
                    personaListViewModel.displayOrder
                )(a.value, b.value)
            ))
    );

    return sortedKeys.reduce<PersonaListEntry[]>(
        (grouped: PersonaListEntry[], group: string): PersonaListEntry[] => {
            return grouped
                .concat(toGroupIndex(group, sorting, emptyMarker))
                .concat(groupedEntries[group]);
        },
        []
    );
}

export function toPersonaListElement(persona: HubPersona): PersonaEntry {
    return { kind: HubListEntryKind.Content, value: persona };
}

function toGroupIndex(groupLabel: string, sorting: Sorting, emptyMarker: string): PersonaGroup {
    if (groupLabel !== emptyMarker) {
        return {
            kind: HubListEntryKind.Group,
            index: adjustedGroupLabel(groupLabel, sorting),
        };
    }
    switch (sorting) {
        case Sorting.ByFirstName:
        case Sorting.ByLastName:
        case Sorting.ByRelevance:
        case Sorting.ByName:
            return {
                kind: HubListEntryKind.Group,
                index: NO_INITIAL_INDICATOR,
            };
        case Sorting.ByCompany:
            return {
                kind: HubListEntryKind.Group,
                index: loc(noCompanyGroupHeader),
                isEmptyRepresentation: true,
            };
        case Sorting.ByHomeCity:
        case Sorting.ByWorkCity:
            return {
                kind: HubListEntryKind.Group,
                index: loc(noCityGroupHeader),
                isEmptyRepresentation: true,
            };
        case Sorting.ByRecentlyAdded:
        case Sorting.ByDeletedDate:
            return {
                kind: HubListEntryKind.Group,
                index: loc(noDateGroupHeader),
                isEmptyRepresentation: true,
            };
        default:
            throw new Error('Not yet implemented');
    }
}

function getComparePersonasFunction(
    sorting: Sorting,
    locale: string,
    displayOrder: DisplayOrder
): (left: HubPersona, right: HubPersona) => number {
    switch (sorting) {
        case Sorting.ByName:
        case Sorting.ByFirstName:
            return compareByGivenNameAndSurname(locale, displayOrder);
        case Sorting.ByLastName:
        case Sorting.ByCompany: // Sort by surname when grouping by company
            return compareBySurnameAndGivenName(locale, displayOrder);
        case Sorting.ByHomeCity:
            return compareByHomeCity(locale, displayOrder);
        case Sorting.ByWorkCity:
            return compareByWorkCity(locale, displayOrder);
        case Sorting.ByRecentlyAdded:
            return compareByRecentlyAdded(locale, displayOrder);
        case Sorting.ByRelevance:
            return compareByRelevancy();
        case Sorting.ByDeletedDate:
            return compareByDeletedDate(locale);
        default:
            throw new Error('Not yet implemented');
    }
}

function filterPersonas(personas: HubPersona[], filter: Filter): HubPersona[] {
    switch (filter) {
        case Filter.All:
            return personas;
        case Filter.HubPerson:
            return personas.filter(persona => persona.type === PersonType.Person);
        case Filter.Contact:
            return personas.filter(
                persona => persona.type === PersonType.Person && !persona.isReadOnly
            );
        case Filter.Organization:
            return personas.filter(
                persona => persona.type === PersonType.Person && persona.info.GALContactGuid
            );
        case Filter.Group:
            return personas.filter(persona => persona.type === PersonType.Group);
        case Filter.OwnedGroup:
            return personas.filter(persona => persona.type === PersonType.Group && persona.isOwner);
        case Filter.JoinedGroup:
            return personas.filter(
                persona => persona.type === PersonType.Group && persona.isMember
            );
        case Filter.List:
            return personas.filter(
                persona =>
                    persona.type === PersonType.PrivateDistributionList ||
                    persona.type === PersonType.PublicDistributionList
            );
        case Filter.PrivateDistributionList:
            return personas.filter(persona => persona.type === PersonType.PrivateDistributionList);
        case Filter.PublicDistributionList:
            return personas.filter(persona => persona.type === PersonType.PublicDistributionList);
        default:
            throw new Error(`${filter} is not a valid Filter enum`);
    }
}

function groupingFunction(
    sorting: Sorting,
    emptyMarker: string,
    culture: string
): (entity: HubPersona) => string {
    switch (sorting) {
        case Sorting.ByFirstName:
        case Sorting.ByLastName:
        case Sorting.ByName:
            return (entity: HubPersona) => initial(entity, sorting, emptyMarker);
        case Sorting.ByCompany:
            return (entity: HubPersona) =>
                entity.type === PersonType.Person && entity.info.company
                    ? entity.info.company
                    : emptyMarker;
        case Sorting.ByHomeCity:
            return (entity: HubPersona) =>
                entity.type === PersonType.Person && entity.info.homeCity
                    ? entity.info.homeCity
                    : emptyMarker;
        case Sorting.ByWorkCity:
            return (entity: HubPersona) =>
                entity.type === PersonType.Person && entity.info.workCity
                    ? entity.info.workCity
                    : emptyMarker;
        case Sorting.ByRecentlyAdded:
            return (entity: HubPersona) =>
                entity.creationTime
                    ? dateForSorting(entity.creationTime, emptyMarker)
                    : emptyMarker;
        case Sorting.ByDeletedDate:
            return (entity: DeletedPersona) =>
                entity.deletedDate ? dateForSorting(entity.deletedDate, emptyMarker) : emptyMarker;
        case Sorting.ByRelevance:
            // ByRelevance does not use grouping.
            return () => '';
        default:
            throw new Error('Not yet implemented');
    }
}

export function getPersonaForFavoritePersona(
    favorite: FavoriteEntity,
    hubPersonasMap: HubPersonasMap,
    unifiedGroupsMap: UnifiedGroupsMap,
    allContactFolderPersonas: HubPersona[]
): Person | PrivateDistributionList | UnifiedGroup | undefined {
    if (favorite.type === PersonType.FavoritePerson) {
        if (favorite.personaId) {
            // If this favorite is backed by a person, then return that person
            const hubPersonaIdForMap = makeHubPersonaId(favorite.personaId);
            const favoritePerson = hubPersonasMap[hubPersonaIdForMap];

            if (favoritePerson && favoritePerson.type === PersonType.Person) {
                return favoritePerson;
            }
        } else {
            // If the person does not have a personaId, or was not found using personaId, then check whether
            // the favorite's email is found in the personasMap, as this favorite might now be a person
            const person =
                favorite.info.mainEmailAddress &&
                allContactFolderPersonas.find(
                    folderHubPersona =>
                        folderHubPersona.type === PersonType.Person &&
                        folderHubPersona.info.mainEmailAddress === favorite.info.mainEmailAddress
                );

            // If a person was found, return it
            if (person && person.type === PersonType.Person) {
                return person;
            }
        }
    } else if (favorite.type === PersonType.FavoritePrivateDistributionList) {
        // It's favorite PDL
        const pdl = allContactFolderPersonas.find(
            folderHubPersona =>
                folderHubPersona.type === PersonType.PrivateDistributionList &&
                folderHubPersona.itemId === favorite.info.itemId
        ) as PrivateDistributionList;

        return pdl;
    } else if (favorite.type === PersonType.FavoriteUnifiedGroup && unifiedGroupsMap) {
        const hubPersonaIdForMap = makeHubGroupPersonaId(
            favorite.info.externalDirectoryObjectId,
            favorite.info.emailAddress
        );

        return unifiedGroupsMap[hubPersonaIdForMap];
    }

    return undefined;
}

export function getPersonaListLoadingState(
    isLoading: boolean,
    hasError: boolean
): PersonaListLoadingState {
    if (isLoading) {
        return PersonaListLoadingState.Loading;
    } else if (hasError) {
        return PersonaListLoadingState.Error;
    }

    return PersonaListLoadingState.Results;
}
